import React, { FC, ReactNode, useMemo } from 'react';
import { Button, Typography, IconButton, Box, Stack, ToggleButton, ToggleButtonGroup, Switch, FormControlLabel } from '@mui/material';
import { Add } from '@mui/icons-material';
import { GuideCommentStatus } from '../__generated__/graphql';
import { StyledPaper } from '../theme';
import { CommentThreadList, RichTextComments, Thread, ThreadActions } from './CommentThread';

/**
 * The "Kommentarer" card on a guide page and on a category page: internal comments and customer
 * feedback in one list, with the "Kun eksterne" switch and the open/closed toggle. The caller
 * supplies the mutations (`actions`), the add handler and the dialog element as `children`.
 */
export const CommentsCard: FC<{
  comments: Thread[];
  actions: ThreadActions;
  onAdd: () => void;
  children?: ReactNode;
  /** Off where there is no customer feedback (the dictionary). */
  showExternalFilter?: boolean;
  richText?: boolean;
  title?: string;
  bare?: boolean;
  composer?: ReactNode;
}> = ({ comments, actions, onAdd, children, showExternalFilter = true, richText = false, title = 'Kommentarer', bare = false, composer }) => {
  const [showUnresolved, setShowUnresolved] = React.useState(true);
  const [onlyExternal, setOnlyExternal] = React.useState(false);

  const numberOfOpenThreads = useMemo(() => comments.filter((comment) => comment.status === GuideCommentStatus.Open).length, [comments]);
  const numberOfClosedThreads = useMemo(() => comments.filter((comment) => comment.status === GuideCommentStatus.Closed).length, [comments]);
  const filteredThreads = useMemo(
    () =>
      comments.filter(
        (comment) =>
          comment.status === (showUnresolved ? GuideCommentStatus.Open : GuideCommentStatus.Closed) && (!onlyExternal || !!comment.externalUserEmail),
      ),
    [comments, showUnresolved, onlyExternal],
  );

  const Container = bare ? Box : StyledPaper;
  return (
    <Container>
      <Box display="grid" gridTemplateColumns="1fr auto" alignItems="center" gap={2} mb={1}>
        {bare ? (
          <Button startIcon={<Add />} onClick={onAdd} sx={{ justifySelf: 'start' }}>
            Ny kommentar
          </Button>
        ) : (
          <Stack direction="row" alignItems="center">
            <Typography variant="h5" component="h3">
              {title}
            </Typography>
            <IconButton onClick={onAdd} aria-label="Ny kommentar">
              <Add />
            </IconButton>
          </Stack>
        )}
        <Stack direction="row" alignItems="center">
          {showExternalFilter && (
            <FormControlLabel control={<Switch checked={onlyExternal} onChange={() => setOnlyExternal(!onlyExternal)} />} label="Kun eksterne" />
          )}
          <ToggleButtonGroup
            color="primary"
            value={showUnresolved ? 'open' : 'resolved'}
            size="small"
            exclusive
            onChange={(_e, newTab) => {
              if (newTab) setShowUnresolved(newTab === 'open');
            }}>
            <ToggleButton value="open">Åpne ({numberOfOpenThreads})</ToggleButton>
            <ToggleButton value="resolved">Lukkede ({numberOfClosedThreads})</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Box>
      {composer}
      {filteredThreads.length === 0 && <Typography sx={{ mt: 2 }}>Ingen {showUnresolved ? 'åpne' : 'lukkede'} kommentarer</Typography>}
      <RichTextComments.Provider value={richText}>
        <CommentThreadList comments={filteredThreads} actions={actions} />
      </RichTextComments.Provider>
      {children}
    </Container>
  );
};
